import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, ExternalLink, Loader2, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobberAdmin() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [params] = useSearchParams();
  const justConnected = params.get('status') === 'connected';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/jobber/status`);
      setStatus(data);
    } catch (e) {
      toast.error('Could not load Jobber status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const beginConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/jobber/oauth/start`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start OAuth. Check backend env config.');
      setConnecting(false);
    }
  };

  const backendOrigin = process.env.REACT_APP_BACKEND_URL || '';
  const webhookUrl = `${backendOrigin}/api/webhooks/jobber`;
  const redirectUri = `${backendOrigin}/api/jobber/oauth/callback`;

  const copy = async (v, label) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success(`${label} copied`);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="bg-ink min-h-screen text-cream">
      <SEO title="Jobber Integration | Admin" description="Connect Jobber." noindex />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-cream/60 hover:text-gold text-sm uppercase tracking-widest mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="eyebrow">Admin &middot; Integrations</div>
        <h1 className="section-title">Jobber connection.</h1>
        <p className="section-lede">
          Connect your Jobber account to auto-push every website lead into Jobber and trigger review
          emails when you mark a job complete.
        </p>

        {justConnected && (
          <div className="mt-6 p-4 rounded-md border border-gold/40 bg-gold/10 text-cream flex items-center gap-3" data-testid="jobber-just-connected">
            <CheckCircle2 className="w-5 h-5 text-gold" />
            <span>Jobber connected successfully.</span>
          </div>
        )}

        <div className="mt-10 rounded-md border border-white/10 bg-ink-panel p-6">
          <div className="flex items-center justify-between">
            <div className="text-cream/60 text-xs uppercase tracking-widest">Current status</div>
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-gold" /> : null}
          </div>

          {status && (
            <div className="mt-3 space-y-2 text-sm" data-testid="jobber-status-panel">
              <StatusRow label="App credentials in .env" ok={status.configured} />
              <StatusRow label="Account connected (OAuth complete)" ok={status.connected} />
              <div className="pt-2 text-cream/50">
                GraphQL version: <span className="text-cream">{status.graphql_version}</span>
              </div>
              {status.connected_at && (
                <div className="text-cream/50">
                  Connected at: <span className="text-cream">{status.connected_at}</span>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={beginConnect}
              disabled={!status?.configured || connecting}
              data-testid="jobber-connect-btn"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {connecting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> :
                status?.connected ? 'Reconnect Jobber' : 'Connect Jobber'}
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </button>
            <button type="button" onClick={load} className="btn-outline">Refresh status</button>
          </div>
        </div>

        <div className="mt-8 rounded-md border border-white/10 bg-ink-panel p-6">
          <div className="text-cream/60 text-xs uppercase tracking-widest">Setup checklist</div>
          <ol className="mt-3 space-y-4 text-sm text-cream/85 list-decimal list-inside">
            <li>
              In Jobber → <strong>Developer Center → Apps → New App</strong>. Enter a name.
            </li>
            <li>
              Set the <strong>Redirect URI</strong> to:
              <UriBlock value={redirectUri} onCopy={() => copy(redirectUri, 'Redirect URI')} testId="copy-redirect-uri" />
            </li>
            <li>
              Set the <strong>Webhook URL</strong> to:
              <UriBlock value={webhookUrl} onCopy={() => copy(webhookUrl, 'Webhook URL')} testId="copy-webhook-url" />
              Subscribe to at minimum <code className="text-gold">JOB_COMPLETE</code> (also{' '}
              <code className="text-gold">JOB_UPDATE</code> and <code className="text-gold">APP_DISCONNECT</code>).
            </li>
            <li>
              Copy the app&rsquo;s <strong>Client ID</strong> and <strong>Client Secret</strong>{' '}
              and paste them into <code className="text-gold">backend/.env</code> as
              <code className="block mt-1 text-gold text-xs">
                JOBBER_CLIENT_ID=&hellip;<br />
                JOBBER_CLIENT_SECRET=&hellip;<br />
                JOBBER_REDIRECT_URI={redirectUri}
              </code>
            </li>
            <li>Restart the backend (or hit Publish) → come back here → click <strong>Connect Jobber</strong>.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, ok }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? <CheckCircle2 className="w-4 h-4 text-gold" /> : <XCircle className="w-4 h-4 text-cream/40" />}
      <span className={ok ? 'text-cream' : 'text-cream/60'}>{label}</span>
    </div>
  );
}

function UriBlock({ value, onCopy, testId }) {
  return (
    <div className="mt-1.5 flex items-center gap-2 rounded border border-white/10 bg-ink px-3 py-2">
      <code className="text-gold text-xs break-all flex-1">{value || '—'}</code>
      <button type="button" onClick={onCopy} data-testid={testId} className="text-cream/70 hover:text-gold" aria-label="Copy">
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}
